'use client';

import type { ReactNode } from 'react';
import { tv } from '../_lib/utils';
import { styleTypes as textStyleTypes, Text } from '../Text';
import { useInView } from 'react-intersection-observer';
import CountUp from 'react-countup';
import { leftCenterValues, smallMediumLargeValues } from '../_lib/designTypesStore';

const styles = tv({
	slots: {
		card: 'rounded-md bg-background-brand-opacity-5',
		label: 'text-text-secondary',
	},
	variants: {
		alignment: {
			left: {
				card: 'py-responsive-sm px-responsive-md',
				label: 'mb-responsive-lg',
			},
			center: {
				card: 'py-responsive-lg px-responsive-md text-center',
				label: 'mb-responsive-3xs',
			},
		},
	},
});

export type StatisticProps = {
	className?: string;
	label?: string;
	value?: string | number;
	description?: ReactNode;
	alignment?: (typeof leftCenterValues)[number];
	size?: (typeof smallMediumLargeValues)[number];
};

export function Stat(props: StatisticProps) {
	const alignment: (typeof leftCenterValues)[number] = props.alignment ?? 'left';
	const size: (typeof smallMediumLargeValues)[number] = props.size ?? 'medium';

	const { card, label } = styles({ alignment });

	const { ref, inView } = useInView({
		threshold: 1,
		triggerOnce: true,
	});

	return (
		<div className={card({ className: props.className })} data-component='Stat'>
			{props.label && (
				<Text className={label()} styleType='label-sm'>
					{props.label}
				</Text>
			)}

			{props.value && (
				<blockquote ref={ref}>
					<Text className='text-text-brand mb-spacing-5xs' styleType={getValueSize(size)}>
						{typeof props.value === 'number' ? <CountUp end={inView ? props.value : 0} /> : props.value}
					</Text>
				</blockquote>
			)}

			{props.description && (
				<Text as='figcaption' styleType={getDescriptionSize(size)}>
					{props.description}
				</Text>
			)}
		</div>
	);
}

function getValueSize(size: (typeof smallMediumLargeValues)[number]): (typeof textStyleTypes)[number] {
	switch (size) {
		case 'small':
			return 'stat-sm';
		case 'medium':
			return 'stat-md';
		case 'large':
			return 'stat-lg';
	}
}

function getDescriptionSize(size: (typeof smallMediumLargeValues)[number]): (typeof textStyleTypes)[number] {
	switch (size) {
		case 'small':
			return 'body-sm-medium';
		case 'medium':
			return 'body-md-medium';
		case 'large':
			return 'body-lg-medium';
	}
}
